import React, { useState, useEffect } from 'react';
import { Button } from './Button.tsx';
import { ScoreGauge } from './ScoreGauge.tsx';
import { analyzeText, humanizeText } from '../services/geminiService.ts';
import { AnalysisResult, ProcessingState, HistoryItem } from '../types.ts';
import { FileText, Trash2, ShieldCheck, Fingerprint, Info, ChevronDown, Activity, Sparkles, Terminal, Zap, Shield, Search, Lock } from 'lucide-react';
import { Toast, ToastType } from './Toast.tsx';
import { motion, AnimatePresence } from 'framer-motion';
// @ts-ignore
import confetti from 'canvas-confetti';
import { PricingModal } from './PricingModal.tsx';

type Mode = 'Standard' | 'Academic' | 'Creative' | 'Stealth';

export const Dashboard: React.FC = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [processState, setProcessState] = useState<ProcessingState>({ status: 'idle' });
  const [showHumanized, setShowHumanized] = useState(false);
  const [humanizedText, setHumanizedText] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<Mode>('Standard');
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const [toast, setToast] = useState<{message: string, type: ToastType} | null>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);

  const WORD_LIMIT = 500;

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type });
  };

  const handleAnalyze = async () => {
    const textToAnalyze = showHumanized && humanizedText ? humanizedText : text;
    if (!textToAnalyze.trim()) return;
    
    setProcessState({ status: 'analyzing' });
    setResult(null); 
    
    try {
      const analysis = await analyzeText(textToAnalyze);
      setResult(analysis);
      setProcessState({ status: 'complete' });
      if (analysis.humanScore > 85) {
          confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ['#22d3ee', '#2ecc71'] });
          showToast("Forensic Analysis Complete: Trusted", 'success');
      }
    } catch (err: any) {
      setProcessState({ status: 'idle', error: err.message });
      showToast(err.message, 'error');
    }
  };

  const handleHumanize = async () => {
    if (!text.trim()) return;
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount > WORD_LIMIT) {
        setShowPricingModal(true);
        return;
    }

    setProcessState({ status: 'humanizing' });
    setResult(null); 

    try {
      const sourceText = showHumanized && humanizedText ? humanizedText : text;
      const newText = await humanizeText(sourceText, selectedMode);
      setHumanizedText(newText);
      setShowHumanized(true);
      const newAnalysis = await analyzeText(newText);
      setResult(newAnalysis);
      setProcessState({ status: 'complete' });
      showToast("Linguistic Restructuring Complete", "success");
    } catch (err: any) {
      setProcessState({ status: 'idle', error: err.message });
      showToast(err.message, 'error');
    }
  };

  const isProcessing = processState.status === 'analyzing' || processState.status === 'humanizing';
  const currentViewText = showHumanized && humanizedText ? humanizedText : text;
  const currentWordCount = currentViewText.trim() ? currentViewText.trim().split(/\s+/).length : 0;

  return (
    <div className="max-w-7xl mx-auto px-0 py-0">
      <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />
      {toast && <div className="fixed bottom-10 right-10 z-[100]"><Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} /></div>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch min-h-[700px]">
        
        {/* Forensic Terminal (Main Text Area) */}
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-8 glass-card rounded-none overflow-hidden flex flex-col relative group"
        >
            {/* Terminal Header */}
            <div className="h-14 border-b border-white/5 flex items-center justify-between px-6 bg-white/5">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <Terminal className="w-4 h-4 text-primary" />
                        <span className="text-[10px] font-mono font-black text-primary uppercase tracking-[0.2em]">Live_Workspace_v3.2</span>
                    </div>
                    <div className="h-4 w-[1px] bg-white/10"></div>
                    <div className="relative">
                        <button 
                            onClick={() => setIsModeDropdownOpen(!isModeDropdownOpen)}
                            className="text-[10px] font-mono font-bold text-subtext hover:text-white transition-colors flex items-center gap-2 uppercase tracking-widest"
                        >
                            Algorithm: <span className="text-primary">{selectedMode}</span>
                            <ChevronDown className={`w-3 h-3 transition-transform ${isModeDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                            {isModeDropdownOpen && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 5 }}
                                    className="absolute top-full left-0 mt-3 w-48 glass-card rounded-none z-50 border border-primary/20"
                                >
                                    {(['Standard', 'Academic', 'Creative', 'Stealth'] as Mode[]).map((m) => (
                                        <button 
                                            key={m} 
                                            onClick={() => { setSelectedMode(m); setIsModeDropdownOpen(false); }}
                                            className={`w-full text-left px-4 py-3 text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-primary/10 transition-colors ${selectedMode === m ? 'text-primary' : 'text-subtext'}`}
                                        >
                                            {m}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={() => setText('')} className="text-subtext hover:text-danger transition-colors" title="Wipe Data">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Input Container */}
            <div className="flex-grow relative bg-black/20">
                {isProcessing && <div className="scanline"></div>}
                <textarea
                    className="w-full h-full p-10 bg-transparent text-white text-lg leading-relaxed resize-none focus:outline-none placeholder-white/10 font-medium selection:bg-primary/30"
                    placeholder="LOAD SOURCE MATERIAL HERE_ [CMD+V]"
                    value={currentViewText}
                    onChange={(e) => { 
                        setText(e.target.value); 
                        if(showHumanized) setShowHumanized(false);
                        if(result) setResult(null);
                    }}
                    disabled={isProcessing}
                />
                
                {/* Data Stats overlay */}
                <div className="absolute bottom-6 left-10 flex gap-6 pointer-events-none">
                    <div className="flex flex-col">
                        <span className="text-[8px] font-mono font-black text-white/20 uppercase tracking-widest">Token_Count</span>
                        <span className="text-xs font-mono font-bold text-primary">{currentWordCount} / {WORD_LIMIT}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[8px] font-mono font-black text-white/20 uppercase tracking-widest">System_Status</span>
                        <span className="text-xs font-mono font-bold text-secondary">{processState.status === 'idle' ? 'STANDBY' : processState.status.toUpperCase()}</span>
                    </div>
                </div>
            </div>

            {/* Terminal Actions */}
            <div className="p-8 border-t border-white/5 flex justify-end gap-4 bg-white/5">
                <Button variant="outline" onClick={handleAnalyze} isLoading={processState.status === 'analyzing'}>
                    Execute Scan
                </Button>
                <Button variant="primary" onClick={handleHumanize} isLoading={processState.status === 'humanizing'}>
                    Stealth Humanize
                </Button>
            </div>
        </motion.div>

        {/* Intelligence Sidebar */}
        <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-4 flex flex-col gap-8"
        >
            {/* TrustScore Display */}
            <div className="glass-card p-10 flex flex-col items-center relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                    <ShieldCheck className="w-32 h-32 text-white" />
                </div>
                
                <h3 className="text-[10px] font-mono font-black text-subtext uppercase tracking-[0.4em] mb-10 text-center">Trust_Score_Verdict</h3>
                
                <AnimatePresence mode="wait">
                    {result ? (
                        <motion.div 
                            key="result"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="flex flex-col items-center"
                        >
                            <ScoreGauge score={result.humanScore} />
                            <div className="mt-10 text-center">
                                <span className={`text-xl font-mono font-black uppercase tracking-[0.1em] ${result.humanScore > 75 ? 'text-secondary' : 'text-danger'}`}>
                                    {result.humanScore > 75 ? 'SAFE_AUTHENTIC' : 'HIGH_AI_DETECTION'}
                                </span>
                                <p className="text-[10px] font-mono text-white/30 mt-2 uppercase tracking-widest">Linguistic Integrity Confirmed</p>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div 
                            key="idle"
                            className="py-12 flex flex-col items-center opacity-40 grayscale"
                        >
                            <Fingerprint className="w-16 h-16 text-primary mb-4 animate-pulse" />
                            <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Awaiting_Data_Input</span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Forensic metrics */}
            {result && (
                <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="glass-card p-8 flex flex-col gap-6"
                >
                    <div className="flex items-center gap-3">
                        <Activity className="w-4 h-4 text-primary" />
                        <h4 className="text-[10px] font-mono font-black uppercase tracking-[0.2em]">Forensic_Sub_Metrics</h4>
                    </div>

                    <div className="space-y-6">
                        <div className="space-y-2">
                            <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest font-bold">
                                <span>Perplexity</span>
                                <span className="text-primary">{result.perplexity}</span>
                            </div>
                            <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                                <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: result.perplexity === 'High' ? '100%' : '40%' }}
                                    className="h-full bg-primary shadow-[0_0_10px_rgba(34,211,238,0.5)]"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest font-bold">
                                <span>Burstiness</span>
                                <span className="text-secondary">{result.burstiness}</span>
                            </div>
                            <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                                <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: result.burstiness === 'High' ? '100%' : '45%' }}
                                    className="h-full bg-secondary shadow-[0_0_10px_rgba(74,222,128,0.5)]"
                                />
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Tips / Info */}
            <div className="mt-auto glass-card p-6 border-l-2 border-primary/30">
                <div className="flex items-start gap-4">
                    <Info className="w-4 h-4 text-primary mt-1" />
                    <div>
                        <span className="text-[10px] font-mono font-black text-white/40 uppercase tracking-widest">Operational_Tip</span>
                        <p className="text-xs text-subtext leading-relaxed mt-1 italic">
                            "Use Stealth mode for high-stakes submissions. It increases perplexity by 40% across all LLM detection models."
                        </p>
                    </div>
                </div>
            </div>
        </motion.div>
      </div>
    </div>
  );
};